import {
    Table,
    TableHead,
    TableRow,
    TableHeaderCell,
    TableCell,
    TableBody
} from "@/components/atoms/Table";
import { useEffect, useMemo, useState } from "react";
import { DataTableProps } from "./DataTable.types";
import { convertToCSV, downloadCSV, getValue } from "@/utils/downloadCSV";
import { Input } from "@/components/atoms/Input";
import { Button } from "@/components/atoms/Button";
import { ArrowDownIcon, ArrowDownTrayIcon, ArrowUpIcon } from "@heroicons/react/24/outline";
import { FormStatusEnum } from "../FormWrapper/FormWrapper.types";
import { cn } from "@/utils/cn";
import { Pagination } from "@/components/atoms/Pagination";
import { downloadExcel } from "@/utils/downloadExcel";
import { useMobile } from "@/hooks/useMobile";

export const DataTable = <T,>({
    data,
    columns,
    tableHeadClass,
    pageSize = 10,
    defaultSortKey,
    defaultSortOrder = "asc",
    changeFormStatus,
    selectItem,
}: DataTableProps<T>) => {
    const [sortKey, setSortKey] = useState<string>(
        defaultSortKey ?? (columns.length > 0 ? columns[0].accessor : "")
    );
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">(defaultSortOrder);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const isMobile = useMobile();

    useEffect(() => {
        setCurrentPage(1);
    }, [data, searchTerm]);

    // 1️⃣ Filtrado sobre TODO el dataset (data)
    const filteredData = useMemo(() => {
        if (!searchTerm) return data;
        return data.filter((row) =>
            columns.some((col) => {
                const raw = getValue(row, col.accessor);
                const valueForFilter = col.filterValue ? col.filterValue(raw, row) : raw;
                return valueForFilter?.toString().toLowerCase().includes(searchTerm.toLowerCase());
            })
        );
    }, [searchTerm, data, columns]);

    // 2️⃣ Ordenar después de filtrar
    const sortedData = useMemo(() => {
        if (!sortKey) return filteredData;
        return [...filteredData].sort((a, b) => {
            const aVal = getValue(a, sortKey);
            const bVal = getValue(b, sortKey);
            if (aVal == null) return 1;
            if (bVal == null) return -1;
            if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
            if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
            return 0;
        });
    }, [filteredData, sortKey, sortOrder]);

    // 3️⃣ Paginar después de ordenar
    const totalPages = Math.ceil(sortedData.length / pageSize);
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return sortedData.slice(start, start + pageSize);
    }, [sortedData, currentPage, pageSize]);

    const handleSort = (key: string) => {
        if (sortKey === key) {
            setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortOrder("asc");
        }
    };

    return (
        <div className="space-y-5">
            <div className="flex justify-between items-center space-x-2">
                <Input
                    className="p-2 border max-w-sm text-sm"
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onClear={() => setSearchTerm("")}
                />
                <div className="flex gap-2">
                    <Button
                        className="text-md"
                        variant="secondary"
                        size="xs"
                        onClick={() =>
                            downloadCSV(convertToCSV(sortedData, columns), "export.csv")
                        }
                    >
                        CSV <ArrowDownTrayIcon className="h-6 w-6" aria-hidden="true" />
                    </Button>
                    <Button
                        className="text-md"
                        variant="excel"
                        size="xs"
                        onClick={() => downloadExcel(sortedData, columns, "export.xlsx")}
                    >
                        Excel <ArrowDownTrayIcon className="h-6 w-6" aria-hidden="true" />
                    </Button>
                </div>
            </div>

            <Table wrapperClassName="shadow-neumorph">
                <TableHead className={tableHeadClass}>
                    <TableRow>
                        {columns.map((col, idx) => (
                            <TableHeaderCell
                                key={idx}
                                className={cn(col.headerClassName, "cursor-pointer select-none")}
                                onClick={() => handleSort(col.accessor)}
                            >
                                <span className="flex items-center gap-1">
                                    {col.header}
                                    {sortKey === col.accessor &&
                                        (sortOrder === "asc" ? (
                                            <ArrowUpIcon className="h-3 w-3" aria-hidden="true" />
                                        ) : (
                                            <ArrowDownIcon className="h-3 w-3" aria-hidden="true" />
                                        ))}
                                </span>
                            </TableHeaderCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {paginatedData.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={columns.length} className="text-center py-4 text-gray-500">
                                Sin datos
                            </TableCell>
                        </TableRow>
                    ) : (
                        paginatedData.map((row, rowIndex) => (
                            <TableRow
                                key={rowIndex}
                                className="cursor-pointer hover:bg-secondary-500 hover:text-white"
                                onDoubleClick={() => {
                                    selectItem && selectItem(row as T);
                                    changeFormStatus(FormStatusEnum.Edit);
                                }}
                                onClick={
                                    isMobile
                                        ? () => {
                                            selectItem && selectItem(row as T);
                                            changeFormStatus(FormStatusEnum.Edit);
                                        }
                                        : undefined
                                }
                            >
                                {columns.map((col, colIndex) => {
                                    const value = getValue(row, col.accessor);
                                    return (
                                        <TableCell
                                            key={colIndex}
                                            className={cn(
                                                "whitespace-nowrap overflow-hidden text-ellipsis max-w-[200px]",
                                                col.cellClassName
                                            )}
                                        >
                                            {col.render ? col.render(value, row) : value}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>

            {totalPages > 1 && (
                <Pagination
                    page={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    showEdges={true}
                />
            )}
        </div>
    );
};

